import { Box, Flex, BoxProps } from '@chakra-ui/react'
import NextLink from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import React from 'react'

import { ChevronLeft, ChevronRight } from '@miimosa/design-system/src/components/icons'

import usePagination, { DOTS } from './usePagination'

type Props = {
  siblingCount?: number
  currentPage: number
  totalPageCount: number
  showArrow?: boolean
}

const Pagination = ({
  siblingCount = 1,
  currentPage,
  totalPageCount,
  showArrow = true,
  ...props
}: Props & BoxProps) => {
  const paginationRange = usePagination({
    currentPage,
    siblingCount,
    totalPageCount,
  })
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const createQueryString = (pageValue: string) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('page', pageValue)
    return params.toString()
  }

  // If there are less than 2 items in pagination range we shall not render the component
  if (paginationRange.length < 2) {
    return null
  }

  const lastPage = paginationRange[paginationRange.length - 1]
  return (
    <>
      <Flex direction="row" columnGap="5px" as="ul" wrap="wrap" justifyContent="center" {...props}>
        {showArrow && currentPage !== 1 && (
          <Box as="li" {...props} py="1">
            <NextLink
              href={`${pathname}?${createQueryString((currentPage - 1).toString())}`}
              rel="canonical"
              aria-label="See previous"
            >
              <Flex
                direction="row"
                align="center"
                justify="center"
                h={{ base: '30px', md: '40px' }}
                w={{ base: '30px', md: '40px' }}
                color="dark_green"
                fontWeight="bold"
                fontSize={['12px', '16px']}
                border="1px"
                borderColor="dark_green"
                borderRadius="4px"
                _hover={{
                  backgroundColor: 'dark_green',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                <ChevronLeft />
              </Flex>
            </NextLink>
          </Box>
        )}
        {paginationRange.map((pageNumber, i) => {
          // If the pageItem is a DOT, render the DOTS
          if (pageNumber === DOTS)
            return (
              <Box as="li" {...props} py="1" key={i}>
                <Flex
                  direction="row"
                  align="center"
                  justify="center"
                  h={{ base: '30px', md: '40px' }}
                  w={{ base: '30px', md: '40px' }}
                  color="dark_green"
                  fontWeight="bold"
                  fontSize={['12px', '16px']}
                  border="1px"
                  borderColor="dark_green"
                  borderRadius="4px"
                >
                  {DOTS}
                </Flex>
              </Box>
            )

          return (
            <Box as="li" {...props} py="1" key={i}>
              <NextLink href={`${pathname}?${createQueryString(pageNumber.toString())}`}>
                <Flex
                  direction="row"
                  align="center"
                  justify="center"
                  h={{ base: '30px', md: '40px' }}
                  w={{ base: '30px', md: '40px' }}
                  color="dark_green"
                  fontWeight="bold"
                  fontSize={['12px', '16px']}
                  border={currentPage !== pageNumber ? '1px' : ''}
                  borderColor="dark_green"
                  borderRadius="4px"
                  _hover={{
                    backgroundColor: 'dark_green',
                    color: 'white',
                  }}
                >
                  {pageNumber}
                </Flex>
              </NextLink>
            </Box>
          )
        })}
        {showArrow && currentPage !== lastPage && (
          <Box as="li" {...props} py="1">
            <NextLink
              href={`${pathname}?${createQueryString((currentPage + 1).toString())}`}
              rel="canonical"
              aria-label="See next"
            >
              <Flex
                direction="row"
                align="center"
                justify="center"
                h={{ base: '30px', md: '40px' }}
                w={{ base: '30px', md: '40px' }}
                color="dark_green"
                fontWeight="bold"
                fontSize={['12px', '16px']}
                border="1px"
                borderColor="dark_green"
                borderRadius="4px"
                _hover={{
                  backgroundColor: 'dark_green',
                  color: 'white',
                }}
              >
                <ChevronRight />
              </Flex>
            </NextLink>
          </Box>
        )}
      </Flex>
    </>
  )
}

export default Pagination
